import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Container, Card, Table, Button, Badge, Spinner, Modal, Form } from "react-bootstrap";
import Swal from "sweetalert2";
import InformacionUsuario from "../modales/InformacionUsuario";
import AvatarCentrado from "../modales/AvatarCentrado";
import FotoINE from "../modales/FotoINE";

import {API_BASE} from "../../services/apirest";

const isNullish = (val) => val == null || (typeof val === "string" && val.trim() === "");

export default function ManagerSolicitudes() {
    const [perfil, setPerfil] = useState(null);
    const [list, setList]   = useState(null);
    const [selected, setSelected] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [filterStatus] = useState("En proceso");
    const [showEditModal, setShowEditModal] = useState(false);
    const [editData, setEditData] = useState(null);
    const [nameFilter, setNameFilter]     = useState("");
    const [rfcFilter, setRfcFilter]       = useState("");
    const ultimosRoles = [
    { id: 8, nombre:  "VENDEDOR (ACTIVE DIRECTORY/SIEBEL TELCOM)" },
    { id: 9, nombre:  "VENDEDOR / INSTALADOR (TOA)" },
    { id: 10, nombre: "SUPERVISOR" },
    { id: 11, nombre: "ADMINISTRATIVO" },
  ];

    const headers = useMemo(() => ({
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
    }), []);

    useEffect(() => {
        fetch(`${API_BASE}/perfil.php`, { headers })
            .then(r => r.json())
            .then(d => {
                if (d.status === "success") setPerfil(d.result.user);
            })
            .catch(console.error);
    }, [headers]);

    const load = useCallback(async () => {
        if (!perfil) return;

        try {
            // 👉 Ahora usamos filterStatus (puede ser "Pendiente", "En proceso", "Registrando", etc.)
            const res = await fetch(
                `${API_BASE}/solicitudes.php?Estado=${encodeURIComponent(filterStatus)}`,
                { method: "GET", headers }
            );
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            let todas = data.status === "success" ? data.result.solicitudes : [];
            if (perfil.rol_id !== 2 && perfil.distribuidor_id) {
                // cualquier otro rol con distribuidor_id sí verá sólo sus registros
                todas = todas.filter(s => Number(s.distribuidor_id) === Number(perfil.distribuidor_id));
            }
            const mustBeNull = [
                "razon_baja",
                "folio_baja",
                "fecha_baja",
                "baja_stage",
                "reactivacion_stage",
                "modificaciones_stage",
                "modificaciones_data"
            ];
            const cleaned = todas.filter(s => {
                const estadoOk = String(s.Estado ?? "").trim().toLowerCase() === "en proceso";
                const statusOk = String(s.status ?? "").trim().toLowerCase() === "activo";
                const extraNulls = mustBeNull.every(f => isNullish(s[f]));
                return estadoOk && statusOk && extraNulls;
            });
            setList(cleaned);
        } catch (err) {
            console.error(err);
            setList([]);
        }
    }, [headers, filterStatus, perfil]);

    useEffect(() => {
        if (perfil) {
            load();
            const poll = setInterval(load, 30000);
            return () => clearInterval(poll);
        }
    }, [load, perfil]);

    const continuar = async (id) => {
        try {
            const res = await fetch(`${API_BASE}/solicitudes.php`, {
                method: "PUT",
                headers,
                body: JSON.stringify({ SolicitudId: id, nuevo_estado: "Registrando" }),
            });
            const d = await res.json();
            if (d.status === "success" || d.status === "ok") {
                Swal.fire("¡LISTO!", "Enviado a Registrando", "success");
                window.dispatchEvent(new Event("solicitudes:refresh"));
                window.dispatchEvent(new Event("inbox:refresh"));
                load();
            } else {
                Swal.fire("Error", d.result?.error_msg || "Fallo al continuar", "error");
            }
        } catch {
            Swal.fire("Error", "No se pudo conectar", "error");
        }
    };

    const handleSaveEdit = async () => {
        try {
            const res = await fetch(`${API_BASE}/solicitudes.php`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(editData),
            });
            const data = await res.json();
            if (data.status === "success") {
                Swal.fire("¡Actualizado!", "La solicitud fue modificada.", "success");
                setShowEditModal(false);
                // Puedes recargar la lista si es necesario
            } else {
                Swal.fire("Error", data.result?.error_msg || "No se pudo actualizar.", "error");
            }
        } catch (err) {
            Swal.fire("Error", "Fallo la petición al servidor.", "error");
        }
    };

    // Exportar datos a CSV para abrir en Excel
    const exportToExcel = () => {
        if (!Array.isArray(list) || list.length === 0) return;

        const headersCsv = [
            "REGION",
            "HUB",
            "PLAZA",
            "NOMBRE COMPLETO",
            "DISTRIBUIDOR",
            "RAZON SOCIAL",
            "KAM",
            "ESTADO",
            "SUPERVISOR INTERNO (izzi)",
            "GERENTE INTERNO (IZZI)",
            "RFC VENDEDOR",
            "USUARIO RED",
            "NO. DE EMPLEADO",
            "FECHA ALTA",
            "FECHA BAJA",
            "SUPERVISOR DISTRIBUIDOR",
            "RFC Supervisor Distribuidor",
            "CLAVE SUPERVISOR",
            "TELEFONO VENDEDOR",
            "CORREO VENDEDOR"
        ];

        const rows = list.map(s => [
            s.region_nombre,
            s.region_hub,
            s.region_plaza,
            `${s.nombre_usuario} ${s.apellido_paterno} ${s.apellido_materno}`.trim(),
            s.distribuidor_nombre,
            s.distribuidor_rs,
            s.distribuidor_kam,
            // Si tu backend sigue usando "Pendiente" para validar:
            s.Estado === "Pendiente" ? "Validado" : s.Estado,
            s.supervisor,
            s.gerente_nombre,
            s.rfc,
            s.red_siebel_telcom || "",
            s.numero_vendedor,// Fecha alta:
            new Date(s.Fecha).toLocaleDateString(),
            // Fecha baja, si existe:
            s.fecha_baja ? new Date(s.fecha_baja).toLocaleDateString() : "",
            s.supervisor_distribuidor || "",
            s.rfc_supervisor_distribuidor || "",
            s.clave_supervisor || "",
            s.telefono || "",
            s.usuario || ""
        ]);

        const csvContent = [
            headersCsv.join(","),
            ...rows.map(row => row.map(val => `"${val}"`).join(","))
        ].join("\r\n");

        const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
        const url  = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        // Puedes nombrar el archivo como quieras:
        link.setAttribute("download", `mis_solicitudes_${filterStatus.toLowerCase()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
    const filteredList = useMemo(() => {
        if (!Array.isArray(list)) return [];
        return list.filter(s => {
            const fullName = `${s.nombre_usuario} ${s.apellido_paterno} ${s.apellido_materno}`.toLowerCase();
            const matchesName = nameFilter
                ? fullName.includes(nameFilter.toLowerCase())
                : true;
            const matchesRfc = rfcFilter
                ? s.rfc.toLowerCase().includes(rfcFilter.toLowerCase())
                : true;
            return matchesName && matchesRfc;
        });
    }, [list, nameFilter, rfcFilter]);

// 3. Ordena sobre filteredList:
    const sortedList = useMemo(() => {
        return [...filteredList].sort((a, b) =>
            new Date(b.Fecha) - new Date(a.Fecha)
        );
    }, [filteredList]);

    if (list === null) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ height: "60vh" }}>
                <Spinner animation="border" />
            </Container>
        );
    }

    return (
        <div className="app-container" >
            <Container className="my-5">
                <div className="mb-3 d-flex align-items-end flex-wrap">
                    <Form.Group controlId="nameFilter" className="me-3 mb-2">
                        <Form.Label>Filtrar por Nombre:</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Nombre..."
                            value={nameFilter}
                            onChange={e => setNameFilter(e.target.value)}
                        />
                    </Form.Group>
                    <Form.Group controlId="rfcFilter" className="me-3 mb-2">
                        <Form.Label>Filtrar por RFC:</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="RFC..."
                            value={rfcFilter}
                            onChange={e => setRfcFilter(e.target.value)}
                        />
                    </Form.Group>
                    <Button
                        variant="success"
                        onClick={exportToExcel}
                        className="mb-2"
                    >
                        Exportar
                    </Button>
                </div>
                <Card className="shadow-sm">
                    <Card.Header className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">Solicitudes en Proceso</h5>
                        <Badge bg={list.length > 0 ? "warning" : "secondary"} pill>
                            {list.length}
                        </Badge>
                    </Card.Header>
                    <Card.Body>
                        {list.length === 0 ? (
                            <p className="text-center text-muted">No hay solicitudes en proceso.</p>
                        ) : (
                            <Table responsive hover striped className="mb-0 align-middle">
                                <thead>
                                <tr>
                                    <th>Distribuidor</th>
                                    <th>Nombre Completo</th>
                                    <th>Teléfono</th>
                                    <th>Fecha</th>
                                    <th>RFC</th>
                                    {filterStatus === "En proceso" && (
                                        <th className="text-center">Acción</th>
                                    )}
                                </tr>
                                </thead>
                                <tbody>
                                {sortedList.map(s => (
                                    <tr key={s.SolicitudId}>
                                        <td>{s.distribuidor_nombre}</td>
                                        <td>{`${s.nombre_usuario} ${s.apellido_paterno} ${s.apellido_materno}`}</td>
                                        <td>{s.telefono}</td>
                                        <td>{new Date(s.Fecha).toLocaleDateString()}</td>
                                        <td>{s.rfc}</td>
                                        {filterStatus === "En proceso" &&(
                                            <td className="text-center">
                                                <Button
                                                    variant="success"
                                                    size="sm"
                                                    onClick={() => {
                                                        setSelected(s);
                                                        setShowDetailModal(true);
                                                    }}
                                                >
                                                    Aprobar
                                                </Button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                                </tbody>
                            </Table>
                        )}
                    </Card.Body>
                </Card>
                {/* Modal de detalle */}
                {selected && (
                    <Modal
                        show={showDetailModal}
                        onHide={() => setShowDetailModal(false)}
                        size="lg"
                        centered
                        dialogClassName="modal-100w"
                        style={{ zIndex: 11000 }}
                    >
                        <Modal.Header closeButton>
                            <Modal.Title>Detalle Solicitud</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <AvatarCentrado src={selected?.foto_url} alt="Foto del vendedor" />
                            <InformacionUsuario
                                user={selected}
                                title="Datos del agente"
                                cols={3}
                                fields={[
                                    { key: "distribuidor_nombre", label: "Distribuidor" },
                                    { key: "distribuidor_rs",     label: "Razón Social" },
                                    { key: "usuario",             label: "Email" },
                                    {
                                        key: "nombre",
                                        label: "Nombre completo",
                                        render: (u) => [u?.nombre_usuario, u?.apellido_paterno, u?.apellido_materno]
                                            .filter(Boolean)
                                            .join(" ")
                                    },
                                    { key: "rfc",                 label: "RFC", render: (u) => (u?.rfc || "").toUpperCase() },
                                    { key: "telefono",            label: "Teléfono" },
                                    { key: "Fecha",               label: "Fecha Ingreso", render: (u) => u?.Fecha ? new Date(u.Fecha).toLocaleDateString("es-MX") : "—" },
                                    { key: "numero_vendedor",     label: "Número Empleado" },
                                    {
                                            key: "rol_id",
                                            label: "Puesto actual",
                                            render: (u) => {
                                            const id = Number(u?.rol_id);
                                            return ultimosRoles.find(r => r.id === id)?.nombre ?? "-";
                                            }
                                },

                                
                                ]}

                                
                            />
                            {selected.situacion_rechazo && (
                                <>
                                    <hr />
                                    <p><strong>Motivo de Rechazo:</strong></p>
                                    <p style={{ whiteSpace: 'pre-wrap' }}>{selected.situacion_rechazo}</p>
                                </>
                            )}

                                {/* 5) Bodega (SOLO si hay datos) */}
                                {(
                                selected?.bodega_distribuidor ||
                                selected?.bodega_almacen ||
                                selected?.bodega_nombre ||
                                selected?.bodega_region ||
                                selected?.bodega_direccion
                                ) && (
                                <InformacionUsuario
                                    user={selected}
                                    title="Bodega"
                                    cols={2}
                                    hideEmpty={true}
                                    fields={[
                                    { key: "bodega_distribuidor", label: "Distribuidor" },
                                    { key: "bodega_almacen",      label: "Almacén" },
                                    { key: "bodega_nombre",       label: "Bodega" },
                                    { key: "bodega_region",       label: "Región" },
                                    { key: "bodega_direccion",    label: "Dirección" },
                                    ]}
                                />
                                )}

                            {selected.ine_url && (
                                <>
                                    <hr />
                                    <FotoINE url={selected?.ine_url} />
                                </>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            {selected.Estado === "En proceso" && (
                                <Button
                                    variant="success"
                                    onClick={() => {
                                        continuar(selected.SolicitudId);
                                        setShowDetailModal(false);
                                    }}
                                >
                                    Aprobar
                                </Button>
                            )}
                            <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
                                Cerrar
                            </Button>
                        </Modal.Footer>
                    </Modal>
                )}
            </Container>

            <Modal
                show={showEditModal}
                onHide={() => setShowEditModal(false)}
                size="lg"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Editar Solicitud</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Nombre</Form.Label>
                            <Form.Control
                                type="text"
                                value={editData?.nombre_usuario || ""}
                                onChange={(e) => setEditData(prev => ({ ...prev, nombre_usuario: e.target.value }))}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Apellido Paterno</Form.Label>
                            <Form.Control
                                type="text"
                                value={editData?.apellido_paterno || ""}
                                onChange={(e) => setEditData(prev => ({ ...prev, apellido_paterno: e.target.value }))}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Apellido Materno</Form.Label>
                            <Form.Control
                                type="text"
                                value={editData?.apellido_materno || ""}
                                onChange={(e) => setEditData(prev => ({ ...prev, apellido_materno: e.target.value }))}
                            />
                        </Form.Group>
                        {/* Supervisor Distribuidor */}
                        <Form.Group className="mb-3">
                            <Form.Label>Supervisor Distribuidor</Form.Label>
                            <Form.Control
                                type="text"
                                value={editData?.supervisor_distribuidor || ""}
                                onChange={(e) => setEditData(prev => ({ ...prev, supervisor_distribuidor: e.target.value }))}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>RFC Supervisor Distribuidor</Form.Label>
                            <Form.Control
                                type="text"
                                value={editData?.rfc_supervisor_distribuidor || ""}
                                onChange={(e) => setEditData(prev => ({ ...prev, rfc_supervisor_distribuidor: e.target.value }))}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Clave Supervisor</Form.Label>
                            <Form.Control
                                type="text"
                                value={editData?.clave_supervisor || ""}
                                onChange={(e) => setEditData(prev => ({ ...prev, clave_supervisor: e.target.value }))}
                            />
                        </Form.Group>

                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                        Cancelar
                    </Button>
                    <Button variant="primary" onClick={handleSaveEdit}>
                        Guardar Cambios
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
